import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { IconInbox, IconMail, IconPhone, IconRefresh } from '../components/icons.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { Button, Pill } from '../components/primitives.jsx';
import { PALETTE } from '../palette.js';

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function readableLabel(value) {
  return String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function displayValue(value) {
  if (Array.isArray(value)) return value.map(displayValue).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value ?? '');
}

function contactName(lead) {
  return lead?.contact?.name || lead?.contact?.email || lead?.contact?.phone || 'Unnamed lead';
}

function sourceName(lead) {
  return lead?.source?.campaign?.name
    || lead?.source?.funnel?.name
    || 'Published funnel';
}

function deliveryPresentation(delivery = {}) {
  const presentations = {
    sent: { label: 'Delivered', tone: 'live' },
    partial: { label: 'Partially delivered', tone: 'warn' },
    sending: { label: 'Sending', tone: 'accent' },
    failed: { label: 'Needs attention', tone: 'warn' },
    pending: { label: 'Queued', tone: 'neutral' },
    not_started: { label: 'Awaiting delivery', tone: 'neutral' },
  };
  return presentations[delivery.status] || presentations.not_started;
}

function formatDate(value, options = {}) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: options.compact ? 'medium' : 'long',
    timeStyle: 'short',
  }).format(date);
}

export function normalizeLeadListResponse(response = {}) {
  return {
    items: Array.isArray(response.items) ? response.items : [],
    page: response.page || { limit: 25, hasMore: false, nextBefore: null },
  };
}

export function summarizeLeadForDisplay(lead = {}) {
  return {
    name: contactName(lead),
    source: sourceName(lead),
    delivery: deliveryPresentation(lead.delivery),
  };
}

export default function Leads({ navigate, route, session }) {
  const companyId = activeCompanyId(session);
  const selectedId = route?.rest?.[0] || '';
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  async function loadLeads() {
    setData(null);
    setError('');
    try {
      const response = await api.get('/api/saas/funnel-leads?limit=25', tenantHeaders(companyId));
      setData(normalizeLeadListResponse(response));
    } catch {
      setError('Leads could not be loaded safely. Try again.');
    }
  }

  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    api.get('/api/saas/funnel-leads?limit=25', tenantHeaders(companyId))
      .then((response) => { if (active) setData(normalizeLeadListResponse(response)); })
      .catch(() => { if (active) setError('Leads could not be loaded safely. Try again.'); });
    return () => { active = false; };
  }, [companyId]);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setDetailError('');
    if (!selectedId) return () => { active = false; };
    api.get(`/api/saas/funnel-leads/${encodeURIComponent(selectedId)}`, tenantHeaders(companyId))
      .then((response) => { if (active) setDetail(response.lead || null); })
      .catch(() => { if (active) setDetailError('This lead is unavailable in the active company.'); });
    return () => { active = false; };
  }, [companyId, selectedId]);

  const selectedLead = useMemo(() => {
    if (selectedId) return detail;
    return data?.items?.[0] || null;
  }, [data, detail, selectedId]);

  async function loadMore() {
    const cursor = data?.page?.nextBefore;
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const response = normalizeLeadListResponse(await api.get(
        `/api/saas/funnel-leads?limit=25&before=${encodeURIComponent(cursor)}`,
        tenantHeaders(companyId),
      ));
      setData((current) => ({
        items: [...new Map([...(current?.items || []), ...response.items].map((lead) => [lead.id, lead])).values()],
        page: response.page,
      }));
    } catch {
      setError('More leads could not be loaded safely. Try again.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="leads-page" style={pageStyle}>
      <style>{responsiveStyles}</style>
      <header className="leads-header" style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Lead inbox</div>
          <h1 style={titleStyle}>Leads</h1>
          <p style={subtitleStyle}>People captured by this company&apos;s published funnels.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadLeads} aria-label="Refresh leads">
          <IconRefresh size={15} /> Refresh
        </Button>
      </header>

      {error && <div role="alert" style={errorStyle}>{error}</div>}

      {!data ? (
        error ? null : <ProtectedPageLoader title="Loading leads" body="Checking the active company before showing captured contacts." />
      ) : data.items.length === 0 ? (
        <div style={emptyStyle}>
          <IconInbox size={30} />
          <h2 style={{ margin: 0, fontSize: 18 }}>No leads yet</h2>
          <p style={{ margin: 0, color: PALETTE.ink2, lineHeight: 1.5 }}>
            New public funnel submissions will appear here for this company.
          </p>
        </div>
      ) : (
        <div className="leads-workbench" style={workbenchStyle}>
          <section className="leads-list" aria-label="Captured leads" style={listStyle}>
            <div style={listHeaderStyle}>
              <span>{data.items.length} captured</span>
              <span>Newest first</span>
            </div>
            {data.items.map((lead) => {
              const presentation = summarizeLeadForDisplay(lead);
              const active = selectedLead?.id === lead.id;
              return (
                <button
                  type="button"
                  key={lead.id}
                  onClick={() => navigate(`leads/${lead.id}`)}
                  aria-current={active ? 'true' : undefined}
                  style={{ ...leadRowStyle, ...(active ? activeLeadRowStyle : {}) }}
                >
                  <span style={avatarStyle}>{presentation.name.slice(0, 1).toUpperCase()}</span>
                  <span style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                    <strong style={truncateStyle}>{presentation.name}</strong>
                    <span style={{ ...truncateStyle, color: PALETTE.ink2 }}>{presentation.source}</span>
                    <span style={{ color: PALETTE.ink3, fontSize: 11.5 }}>{formatDate(lead.createdAt, { compact: true })}</span>
                  </span>
                  <Pill tone={presentation.delivery.tone} style={{ alignSelf: 'start', justifySelf: 'end', fontSize: 9.5, padding: '3px 7px' }}>
                    {presentation.delivery.label}
                  </Pill>
                </button>
              );
            })}
            {data.page?.hasMore && (
              <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore} style={{ margin: 12 }}>
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </section>

          <section className="lead-detail" aria-label="Lead details" style={detailStyle}>
            {selectedId && !detail && !detailError ? (
              <ProtectedPageLoader title="Loading lead" minHeight={420} />
            ) : detailError ? (
              <div role="alert" style={errorStyle}>{detailError}</div>
            ) : selectedLead ? (
              <LeadDetail
                lead={selectedLead}
                showBack={Boolean(selectedId)}
                onBack={() => navigate('leads')}
              />
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}

function LeadDetail({ lead, showBack, onBack }) {
  const presentation = summarizeLeadForDisplay(lead);
  const answers = Object.entries(lead.answers || {});
  const consent = Array.isArray(lead.consent) ? lead.consent : [];
  const attribution = Object.entries(lead.attribution || {});
  const delivery = lead.delivery || {};
  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {showBack && (
        <button type="button" className="lead-mobile-back" onClick={onBack} style={backButtonStyle}>
          Back to leads
        </button>
      )}
      <div style={detailHeaderStyle}>
        <div>
          <div style={eyebrowStyle}>{lead.source?.brand?.name || 'Captured contact'}</div>
          <h2 style={detailTitleStyle}>{presentation.name}</h2>
          <div style={{ color: PALETTE.ink2, marginTop: 4 }}>{formatDate(lead.createdAt)}</div>
        </div>
        <Pill tone={presentation.delivery.tone}>{presentation.delivery.label}</Pill>
      </div>

      <div className="lead-contact-grid" style={contactGridStyle}>
        <ContactValue icon={IconMail} label="Email" value={lead.contact?.email} href={lead.contact?.email ? `mailto:${lead.contact.email}` : null} />
        <ContactValue icon={IconPhone} label="Phone" value={lead.contact?.phone} href={lead.contact?.phone ? `tel:${lead.contact.phone}` : null} />
      </div>

      <DetailSection title="Source">
        <DefinitionRows rows={[
          ['Company', lead.source?.company?.name],
          ['Brand', lead.source?.brand?.name],
          ['Campaign', lead.source?.campaign?.name],
          ['Offer', lead.source?.campaign?.offer],
          ['Funnel', lead.source?.funnel?.name],
          ['Page', lead.sourcePagePath],
        ]} />
      </DetailSection>

      <DetailSection title="Answers" empty={!answers.length}>
        <DefinitionRows rows={answers.map(([key, value]) => [readableLabel(key), displayValue(value)])} />
      </DetailSection>

      <DetailSection title="Consent" empty={!consent.length}>
        <DefinitionRows rows={consent.map((item) => [
          item.label || readableLabel(item.purpose || item.fieldId),
          item.accepted ? 'Accepted' : 'Not accepted',
        ])} />
      </DetailSection>

      <DetailSection title="Attribution" empty={!attribution.length}>
        <DefinitionRows rows={attribution.map(([key, value]) => [readableLabel(key), displayValue(value)])} />
      </DetailSection>

      <DetailSection title="Notification delivery">
        <DefinitionRows rows={[
          ['Status', presentation.delivery.label],
          ['Recipients', delivery.recipients ?? 0],
          ['Delivered', delivery.sent ?? 0],
          ['Failed', delivery.failed ?? 0],
          ['Last attempt', delivery.lastAttemptedAt ? formatDate(delivery.lastAttemptedAt) : 'Not attempted'],
        ]} />
      </DetailSection>
    </div>
  );
}

function ContactValue({ icon: Icon, label, value, href }) {
  const content = value || 'Not provided';
  return (
    <div style={contactValueStyle}>
      <Icon size={17} />
      <div style={{ minWidth: 0 }}>
        <div style={fieldLabelStyle}>{label}</div>
        {href
          ? <a href={href} style={contactLinkStyle}>{content}</a>
          : <div style={{ color: PALETTE.ink2 }}>{content}</div>}
      </div>
    </div>
  );
}

function DetailSection({ title, empty = false, children }) {
  return (
    <section style={sectionStyle}>
      <h3 style={sectionTitleStyle}>{title}</h3>
      {empty ? <p style={{ color: PALETTE.ink3, margin: 0 }}>No information provided.</p> : children}
    </section>
  );
}

function DefinitionRows({ rows }) {
  return (
    <dl style={{ display: 'grid', gap: 0, margin: 0 }}>
      {rows.filter(([, value]) => value !== null && value !== undefined && value !== '').map(([label, value]) => (
        <div key={label} style={definitionRowStyle}>
          <dt style={fieldLabelStyle}>{label}</dt>
          <dd style={definitionValueStyle}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const responsiveStyles = `
  .lead-mobile-back { display: none !important; }
  @media (max-width: 980px) {
    .leads-page { padding: 26px 22px 48px !important; }
    .leads-workbench { grid-template-columns: minmax(260px, 0.8fr) minmax(0, 1.2fr) !important; }
  }
  @media (max-width: 760px) {
    .leads-page { padding: 22px 16px 44px !important; }
    .leads-header { align-items: flex-start !important; }
    .leads-workbench { display: block !important; }
    .leads-list { display: block; }
    .lead-detail { display: none; }
    .leads-workbench:has(.lead-detail .lead-mobile-back) .lead-detail { display: block; }
    .lead-mobile-back { display: inline-flex !important; }
    .leads-workbench:has(.lead-detail .lead-mobile-back) .leads-list { display: none; }
    .lead-contact-grid { grid-template-columns: minmax(0, 1fr) !important; }
  }
`;

const pageStyle = { padding: '34px 40px 64px', maxWidth: 1420, margin: '0 auto', fontFamily: 'Inter, sans-serif', color: PALETTE.ink };
const headerStyle = { display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 20, marginBottom: 24 };
const eyebrowStyle = { color: PALETTE.accentDeep, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' };
const titleStyle = { fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 44, fontWeight: 400, margin: '4px 0 0' };
const subtitleStyle = { color: PALETTE.ink2, margin: '6px 0 0', lineHeight: 1.5 };
const workbenchStyle = { display: 'grid', gridTemplateColumns: 'minmax(300px, 0.72fr) minmax(0, 1.28fr)', minHeight: 680, border: `1px solid ${PALETTE.line}`, borderRadius: 8, overflow: 'hidden', background: PALETTE.surface };
const listStyle = { borderRight: `1px solid ${PALETTE.line}`, minWidth: 0, background: PALETTE.surface };
const listHeaderStyle = { display: 'flex', justifyContent: 'space-between', padding: '13px 16px', color: PALETTE.ink3, fontSize: 11.5, borderBottom: `1px solid ${PALETTE.line}` };
const leadRowStyle = { width: '100%', display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr) auto', gap: 11, padding: 16, textAlign: 'left', border: 0, borderBottom: `1px solid ${PALETTE.lineSoft}`, background: 'transparent', color: PALETTE.ink, cursor: 'pointer', fontFamily: 'Inter, sans-serif' };
const activeLeadRowStyle = { background: PALETTE.accentTint, boxShadow: `inset 3px 0 0 ${PALETTE.accent}` };
const avatarStyle = { width: 36, height: 36, borderRadius: 8, display: 'grid', placeItems: 'center', background: PALETTE.surfaceAlt, color: PALETTE.accentDeep, fontWeight: 700 };
const truncateStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 13 };
const detailStyle = { padding: '26px clamp(20px, 4vw, 42px) 42px', minWidth: 0 };
const detailHeaderStyle = { display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 16 };
const detailTitleStyle = { fontFamily: 'Instrument Serif, serif', fontSize: 34, fontWeight: 400, margin: '5px 0 0', overflowWrap: 'anywhere' };
const contactGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 };
const contactValueStyle = { display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 10, padding: 14, border: `1px solid ${PALETTE.line}`, borderRadius: 8, color: PALETTE.accentDeep };
const contactLinkStyle = { display: 'block', color: PALETTE.ink, overflowWrap: 'anywhere', textDecorationColor: PALETTE.accent };
const sectionStyle = { display: 'grid', gap: 10, borderTop: `1px solid ${PALETTE.line}`, paddingTop: 18 };
const sectionTitleStyle = { fontSize: 15, margin: 0 };
const definitionRowStyle = { display: 'grid', gridTemplateColumns: 'minmax(110px, 0.4fr) minmax(0, 1fr)', gap: 16, padding: '10px 0', borderBottom: `1px solid ${PALETTE.lineSoft}` };
const fieldLabelStyle = { color: PALETTE.ink3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase' };
const definitionValueStyle = { margin: 0, color: PALETTE.ink, overflowWrap: 'anywhere', lineHeight: 1.45 };
const errorStyle = { marginBottom: 18, padding: 13, border: '1px solid #E8C9C1', borderRadius: 8, background: PALETTE.dangerSoft, color: PALETTE.danger };
const emptyStyle = { minHeight: 480, display: 'grid', placeItems: 'center', alignContent: 'center', gap: 10, textAlign: 'center', color: PALETTE.ink3 };
const backButtonStyle = { border: 0, padding: 0, background: 'transparent', color: PALETTE.accentDeep, font: '600 13px Inter, sans-serif', cursor: 'pointer', justifySelf: 'start' };

export const leadsInternalsForTest = Object.freeze({
  contactName,
  deliveryPresentation,
  displayValue,
  readableLabel,
  sourceName,
});
